import type React from "react";
import {
	createContext,
	type ReactNode,
	useContext,
	useMemo,
	useReducer,
} from "react";
import type { TranscriptSegment } from "../components/TranscriptionDisplay";

interface TranscriptionState {
	// Live transcription UI contract:
	// Keep segments in arrival order. Existing IDs are updated in place.
	segments: TranscriptSegment[];
}

type TranscriptionAction =
	| { type: "ADD_SEGMENT"; segment: TranscriptSegment }
	| {
			type: "UPDATE_SEGMENT";
			id: string;
			text: string;
			isFinal?: boolean;
			confidence?: number;
	  }
	| { type: "DELETE_SEGMENT"; id: string }
	| { type: "CLEAR_SEGMENTS" }
	| { type: "SET_SEGMENTS"; segments: TranscriptSegment[] };

type UpdateSegmentAction = Extract<
	TranscriptionAction,
	{ type: "UPDATE_SEGMENT" }
>;

const mergeUpdatedSegment = (
	current: TranscriptSegment,
	action: UpdateSegmentAction,
): TranscriptSegment | null => {
	const updated = {
		...current,
		text: action.text,
		isFinal: action.isFinal ?? current.isFinal,
		confidence: action.confidence ?? current.confidence,
	};

	if (
		current.text === updated.text &&
		current.isFinal === updated.isFinal &&
		current.confidence === updated.confidence
	) {
		return null;
	}

	return updated;
};

const isEquivalentLiveSegment = (
	current: TranscriptSegment,
	incoming: TranscriptSegment,
): boolean => {
	// timestamp is intentionally ignored because interim updates frequently recreate Date objects.
	return (
		current.id === incoming.id &&
		current.text === incoming.text &&
		current.isFinal === incoming.isFinal &&
		current.confidence === incoming.confidence &&
		current.type === incoming.type &&
		current.commenterId === incoming.commenterId
	);
};

const TranscriptionContext = createContext<
	| {
			state: TranscriptionState;
			dispatch: React.Dispatch<TranscriptionAction>;
	  }
	| undefined
>(undefined);

export const transcriptionReducer = (
	state: TranscriptionState,
	action: TranscriptionAction,
): TranscriptionState => {
	switch (action.type) {
		case "ADD_SEGMENT": {
			// Performance optimization: Check the last segment first, as it's the most common update target during live transcription.
			// This avoids O(N) findIndex for every interim result update.
			const lastIndex = state.segments.length - 1;
			if (
				lastIndex >= 0 &&
				state.segments[lastIndex].id === action.segment.id
			) {
				const current = state.segments[lastIndex];
				if (
					current === action.segment ||
					isEquivalentLiveSegment(current, action.segment)
				) {
					return state;
				}
				const newSegments = [...state.segments];
				newSegments[lastIndex] = action.segment;
				return { ...state, segments: newSegments };
			}

			// Fallback: search from the end (optimized for chronological interaction)
			let existingIndex = -1;
			for (let i = state.segments.length - 1; i >= 0; i--) {
				if (state.segments[i].id === action.segment.id) {
					existingIndex = i;
					break;
				}
			}
			if (existingIndex !== -1) {
				const current = state.segments[existingIndex];
				if (
					current === action.segment ||
					isEquivalentLiveSegment(current, action.segment)
				) {
					return state;
				}
				const newSegments = [...state.segments];
				newSegments[existingIndex] = action.segment;
				return { ...state, segments: newSegments };
			}
			return { ...state, segments: [...state.segments, action.segment] };
		}
		case "UPDATE_SEGMENT": {
			// Performance optimization: Check the last segment first to avoid O(N) map.
			const lastIndex = state.segments.length - 1;
			if (lastIndex >= 0 && state.segments[lastIndex].id === action.id) {
				const current = state.segments[lastIndex];
				const updated = mergeUpdatedSegment(current, action);
				if (!updated) {
					return state;
				}
				const newSegments = [...state.segments];
				newSegments[lastIndex] = updated;
				return { ...state, segments: newSegments };
			}

			// Fallback: search from the end (optimized for chronological interaction)
			let existingIndex = -1;
			for (let i = state.segments.length - 1; i >= 0; i--) {
				if (state.segments[i].id === action.id) {
					existingIndex = i;
					break;
				}
			}
			if (existingIndex === -1) {
				return state;
			}
			const current = state.segments[existingIndex];
			const updated = mergeUpdatedSegment(current, action);
			if (!updated) {
				return state;
			}
			const newSegments = [...state.segments];
			newSegments[existingIndex] = updated;
			return { ...state, segments: newSegments };
		}
		case "DELETE_SEGMENT": {
			// Performance optimization: Search from the end as users usually delete recent segments.
			// This avoids O(N) scan and skips re-render (O(1)) if ID is not found.
			let index = -1;
			for (let i = state.segments.length - 1; i >= 0; i--) {
				if (state.segments[i].id === action.id) {
					index = i;
					break;
				}
			}
			if (index === -1) {
				return state;
			}
			const newSegments = [...state.segments];
			newSegments.splice(index, 1);
			return {
				...state,
				segments: newSegments,
			};
		}
		case "CLEAR_SEGMENTS":
			return { ...state, segments: [] };
		case "SET_SEGMENTS":
			return { ...state, segments: action.segments };
		default:
			return state;
	}
};

export const TranscriptionProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [state, dispatch] = useReducer(transcriptionReducer, { segments: [] });

	// Performance optimization: Memoize the context value to prevent unnecessary re-renders of all consumers
	// when the provider itself re-renders but the state/dispatch haven't changed.
	const contextValue = useMemo(() => ({ state, dispatch }), [state]);

	return (
		<TranscriptionContext.Provider value={contextValue}>
			{children}
		</TranscriptionContext.Provider>
	);
};

export const useTranscription = () => {
	const context = useContext(TranscriptionContext);
	if (!context) {
		throw new Error(
			"useTranscription must be used within a TranscriptionProvider",
		);
	}
	return context;
};
